import React, { useMemo, useState } from 'react';
import { MdInfo, MdRefresh } from 'react-icons/md';
import { Box, DrawerBody, DrawerHeader, Flex, HStack, Icon, IconButton, Text, Tooltip } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import DropdownMenu from '../../../../../components/DropdownMenu';
import { analyticsIds } from '../../../../../constants/analyticsIds';
import { useInsuredActiveRecordsData } from '../../../../../services/insured-service';
import insuredKeys from '../../../../../services/models/insured/keys';
import { getInfiniteListData } from '../../../../../services/util';
import colors from '../../../../../styles/theme/foundation/colors';
import { createMap } from '../../../../../utils/arrayUtil';
import { queryClient } from '../../../../../utils/queryClientUtil';
import CloseArrow from '../CorrespondenceTray/components/CloseArrow';

import NotificationHistoryBody from './components/NotificationHistoryBody';

const NotificationsTray = ({ insuredId, recordId }) => {
  const initialRecordId = recordId;
  const { data: activeRecords, isLoading: recordsLoading } = useInsuredActiveRecordsData({ insuredId });

  const [currentRecordId, setCurrentRecordId] = useState(initialRecordId);
  const [currentOrder, setCurrentOrder] = useState('desc');

  // get list of jobs to use for record descriptions
  const jobs = useMemo(() => {
    if (recordsLoading) {
      return [];
    }

    return getInfiniteListData(activeRecords).map(record => ({
      jobName: record.job.jobName,
      id: record.id,
      contractNumber: record.contractNumber
    }));
  }, [activeRecords]);

  const recordsMap = jobs && jobs.map(({ id, jobName, contractNumber }) => {
    let label = jobName;

    if (contractNumber) {
      label = `${jobName} - ${contractNumber}`;
    }

    return {
      id,
      label
    };
  });
  const recordOptions = [{ id: 'all', label: 'All Records' }, ...recordsMap];
  const recordOptionsHash = createMap(recordOptions, 'id');

  const orderOptions = [
    {
      label: 'Newest First',
      id: 1,
      type: 'desc'
    },
    {
      label: 'Oldest First',
      id: 2,
      type: 'asc'
    }
  ];
  const orderOptionsHash = createMap(orderOptions, 'type');

  const handleProjectOptionClick = selected => {
    setCurrentRecordId(selected.value);
  };

  const handleOrderOptionsClick = selected => {
    if (parseInt(selected.value, 10) === 1) {
      setCurrentOrder('desc');
    } else {
      setCurrentOrder('asc');
    }
  };

  return (
    <>
      <CloseArrow />
      <DrawerHeader>
        <Flex justify="space-between" w="100%">
          <Flex direction="column">
            <HStack>
              <Text lineHeight="35px" textStyle="displayXxs">
                Notification History
              </Text>
              <Box>
                <Tooltip label={'This tray shows automated emails sent by PINS to users and external contacts based on activity in the app. '
                  + 'These notifications keep recipients informed about events like submissions, comments, and updates.'}
                >
                  <Box>
                    <Icon as={MdInfo} color={colors.greyScale['600']} h="16px" w="16px" />
                  </Box>
                </Tooltip>
              </Box>
            </HStack>
            <Flex mt={2}>
              <IconButton
                aria-label="Refresh Notification History"
                data-analytics-id={analyticsIds.insuredProfilePage.policiesComponent.emailHistoryTray.refreshEmailHistoryButton}
                icon={<Icon as={MdRefresh} h="16px" w="16px" />}
                mr="2px"
                variant="textGrey"
                onClick={() => {
                  const automatedEmailKey = insuredKeys.insuredAutomatedEmails(insuredId, currentRecordId, currentOrder);

                  queryClient.invalidateQueries(automatedEmailKey);
                }}
              />
              <DropdownMenu
                dataAnalyticsKey={analyticsIds.insuredProfilePage.policiesComponent.emailHistoryTray.sortEmailHistory}
                options={orderOptions}
                selected={orderOptionsHash[currentOrder].label}
                variant="secondary"
                width="140px"
                onSelect={handleOrderOptionsClick}
              />
            </Flex>
          </Flex>
          <DropdownMenu
            dataAnalyticsKey={analyticsIds.insuredProfilePage.policiesComponent.emailHistoryTray.sortEmailHistoryByJob}
            options={recordOptions}
            selected={recordOptionsHash[currentRecordId]?.label}
            size="lg"
            variant="secondary"
            width="240px"
            onSelect={handleProjectOptionClick}
          />
        </Flex>
      </DrawerHeader>
      <DrawerBody>
        <NotificationHistoryBody
          insuredId={insuredId}
          order={currentOrder}
          recordId={parseInt(currentRecordId, 10)}
        />
      </DrawerBody>
    </>
  );
};

NotificationsTray.propTypes = {
  insuredId: PropTypes.string,
  recordId: PropTypes.string
};

export default NotificationsTray;
